import { makeAutoObservable, runInAction } from 'mobx'
import { type IDropdownItem, Typography } from 'shared/ui'
import { logger, numberFormat } from 'shared/lib'
import { type BillingPlan } from 'entities/Billing/model/BillingPlan'
import { BillingApi } from 'entities/Billing'
import { type IResponseGetBillingPreview } from 'entities/Billing/api/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { PlansSelectorStore, type BillingPeriod } from 'pages/settings/ui/BillingPlan'
import styles from './styles.module.scss'

const seatsPrice = 10
const seatsPriceYearly = 100
const numbersPrice = 5
const numbersPriceYearly = 50

export type IPlanSelectionStoreOptions = {
  getSeatsUnitPrice?: () => number
  getNumbersUnitPrice?: () => number
  hideLabelRight?: boolean
}

export class PlanSelectionStore {
  private _period: BillingPeriod = 'annually'
  private _selectedValues: Record<string, string> = {}
  private readonly _plansSelectorStore: PlansSelectorStore
  private _previewPlanData: IResponseGetBillingPreview | null = null
  previewPlanLoading = false

  constructor(
    readonly entities: IEntitiesStore,
    private _options: IPlanSelectionStoreOptions = {}
  ) {
    this._plansSelectorStore = new PlansSelectorStore(entities.billingStore)
    makeAutoObservable(this)
  }

  get period(): BillingPeriod {
    return this._period
  }

  get isMonthly(): boolean {
    return this._period === 'monthly'
  }

  get previewPlanData() {
    return this._previewPlanData
  }

  get selectedValues(): Record<string, string> {
    const values = { ...this._selectedValues }
    const basicPlans = this._plansSelectorStore.getBasicPlans(this._period)
    const proPlans = this._plansSelectorStore.getProPlans(this._period)
    if (!values.Basic && basicPlans.length) {
      values.Basic = basicPlans[0].id
    }
    if (!values.Pro && proPlans.length) {
      values.Pro = proPlans[0].id
    }
    return values
  }

  get selectedBasicPlan(): BillingPlan | null {
    const plans = this._plansSelectorStore.getBasicPlans(this._period)
    const id = this.selectedValues.Basic
    return id ? plans.find((p) => p.id === id) || null : null
  }

  get selectedProPlan(): BillingPlan | null {
    const plans = this._plansSelectorStore.getProPlans(this._period)
    const id = this.selectedValues.Pro
    return id ? plans.find((p) => p.id === id) || null : null
  }

  get additionalSeatPrice(): string {
    const fromOption = this._options.getSeatsUnitPrice?.()
    const price = fromOption || (this.isMonthly ? seatsPrice : seatsPriceYearly)
    return this.isMonthly ? `$${price.toFixed(2)} / month` : `$${price.toFixed(2)} / year`
  }

  get additionalNumbersPrice(): string {
    const fromOption = this._options.getNumbersUnitPrice?.()
    const price = fromOption || (this.isMonthly ? numbersPrice : numbersPriceYearly)
    return this.isMonthly ? `$${price.toFixed(2)} / month` : `$${price.toFixed(2)} / year`
  }

  getBasicPlans = (interval: BillingPeriod): BillingPlan[] => {
    return this._plansSelectorStore.getBasicPlans(interval)
  }

  getProPlans = (interval: BillingPeriod): BillingPlan[] => {
    return this._plansSelectorStore.getProPlans(interval)
  }

  getPlansList = (interval: BillingPeriod): BillingPlan[] => {
    return this._plansSelectorStore.getPlansList(interval)
  }

  formatPrice(plan: BillingPlan | null): string {
    if (!plan) return '$0'
    const price = plan.interval === 'annual' ? plan.origin.monthly_price : plan.origin.monthly_price
    return `${numberFormat({
      value: price,
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`
  }

  getPriceSuffix(plan: BillingPlan | null): string | null {
    return plan?.interval === 'annual' ? ' / month / billed yearly' : ' / month'
  }

  get basicPlansDropdownItems(): IDropdownItem[] {
    const plans = this._plansSelectorStore.getBasicPlans(this._period)
    return this.convertPlansToDropdownItems(plans, this.selectedBasicPlan)
  }

  get proPlansDropdownItems(): IDropdownItem[] {
    const plans = this._plansSelectorStore.getProPlans(this._period)
    return this.convertPlansToDropdownItems(plans, this.selectedProPlan)
  }

  convertPlansToDropdownItems = (
    plans: BillingPlan[],
    selectedPlan: BillingPlan | null
  ): IDropdownItem[] => {
    const selectedPlanPrice = selectedPlan
      ? selectedPlan.interval === 'annual'
        ? selectedPlan.origin.price
        : selectedPlan.origin.monthly_price
      : null
    const currentPlanId = this.entities.subscriptionStore.plan?.id
    const isCanceled = this.entities.subscriptionStore.isCanceled
    const isTrial = this.entities.subscriptionStore.isTrial
    const hideLabelRight = this._options.hideLabelRight

    return plans.map((plan) => {
      const isSelectedPlan = plan.id === selectedPlan?.id
      const isCurrentPlan = plan.id === currentPlanId
      let labelRight: string | undefined

      if (!hideLabelRight && isCurrentPlan && !isCanceled && !isSelectedPlan && !isTrial) {
        labelRight = 'Current plan'
      } else if (!hideLabelRight && !isSelectedPlan && selectedPlanPrice !== null) {
        const planPrice = plan.interval === 'annual' ? plan.origin.price : plan.origin.monthly_price
        const difference = planPrice - selectedPlanPrice
        if (difference !== 0) {
          const sign = difference > 0 ? '+' : '-'
          const formattedDifference = numberFormat({
            value: Math.abs(difference),
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
          labelRight = `${sign}$${formattedDifference.replace('$', '')}`
        }
      }

      return {
        id: plan.id,
        label: plan.labelCreditsByInterval,
        data: plan,
        labelRight: (
          <Typography
            ariaLabel='labelRight'
            variant='body-md-regular'
            color='var(--content-primary-tertiary)'
            tag='div'
            className={styles.labelRight}
          >
            {labelRight}
          </Typography>
        ),
        labelRightVariant: 'body-md-regular' as const,
        labelRightColor: 'var(--content-primary-tertiary)',
      }
    })
  }

  findPlanIndexById = (plans: BillingPlan[], planId: string | undefined): number => {
    if (!planId) return -1
    return plans.findIndex((plan) => plan.id === planId)
  }

  getPlanIdByIndex = (plans: BillingPlan[], index: number): string => {
    if (plans.length === 0) return ''
    if (index >= 0 && index < plans.length) return plans[index].id
    const targetIndex = index >= plans.length ? plans.length - 1 : 0
    return plans[targetIndex].id
  }

  setPeriod = (period: BillingPeriod) => {
    this._period = period
  }

  setSelectedValue = (planName: string, value: string) => {
    this._selectedValues = { ...this._selectedValues, [planName]: value }
  }

  handleSetPeriod = (tab: { key: BillingPeriod }) => {
    const period = tab.key
    const currentBasic = this._plansSelectorStore.getBasicPlans(this._period)
    const currentPro = this._plansSelectorStore.getProPlans(this._period)
    const basicIndex = this.findPlanIndexById(currentBasic, this.selectedValues.Basic)
    const proIndex = this.findPlanIndexById(currentPro, this.selectedValues.Pro)
    this._period = period
    const newBasic = this._plansSelectorStore.getBasicPlans(period)
    const newPro = this._plansSelectorStore.getProPlans(period)
    this._selectedValues = {
      ...this._selectedValues,
      Basic: this.getPlanIdByIndex(newBasic, basicIndex),
      Pro: this.getPlanIdByIndex(newPro, proIndex),
    }
  }

  loadPreviewPlanData = async (params: { plan: string; seats: number; numbers: number }) => {
    if (!params.plan) return
    try {
      this.previewPlanLoading = true
      const { data } = await BillingApi.getPlanData({ params })
      runInAction(() => {
        this._previewPlanData = data
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this.previewPlanLoading = false
      })
    }
  }

  clearPreviewPlanData = () => {
    this._previewPlanData = null
  }

  get previewPlanLines(): Array<{ title: string; value: string }> {
    const formatPrice = (val: number) =>
      numberFormat({
        value: val,
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + (this._period === 'monthly' ? ' / month' : ' / year')

    const data = this._previewPlanData
    if (!data) return []

    const lines: Array<{ title: string; value: string }> = []

    const planValue = this.getPlanLineAmount('plan')
    if (planValue) {
      lines.push({ title: 'Plan', value: formatPrice(planValue / 100) })
    }

    const seatsTotal = data.seats?.free_count ?? 0
    const numbersTotal = data.numbers?.free_count ?? 0
    const seatAmount = (data.seats?.unit_price ?? 0) / 100
    const numAmount = (data.numbers?.unit_price ?? 0) / 100

    if (seatsTotal > 0) {
      lines.push({ title: `Seats × ${seatsTotal}`, value: formatPrice(seatAmount) })
    }
    if (numbersTotal > 0) {
      lines.push({ title: `Numbers × ${numbersTotal}`, value: formatPrice(numAmount) })
    }

    return lines
  }

  getPlanLineAmount = (key: string) => {
    if (!this._previewPlanData) return 0

    const planData = this._previewPlanData.is_downgrade
      ? this._previewPlanData.next_charge_details?.lines
      : this._previewPlanData.lines
    return planData?.find((line) => line.key === key)?.amount || 0
  }

  get previewTotalDueToday() {
    return this.getPlanLineAmount('plan') / 100
  }

  get previewSubtotalValue(): number {
    const data = this._previewPlanData
    if (!data) return 0
    let subtotal = this.previewTotalDueToday
    const seatsDiscount = (data.seats?.unit_price || 0) / 100
    const numbersDiscount = (data.numbers?.unit_price || 0) / 100
    if (seatsDiscount !== 0) subtotal += seatsDiscount
    if (numbersDiscount !== 0) subtotal += numbersDiscount
    return subtotal
  }

  get previewTotalPlanData(): Array<{ title: string; value: number; color?: string }> {
    const data = this._previewPlanData
    if (!data) return []

    const lines: Array<{ title: string; value: number; color?: string }> = [
      { title: 'Subtotal', value: this.previewSubtotalValue },
    ]

    const seatsDiscount = (data.seats?.unit_price ?? 0) / 100
    const numbersDiscount = (data.numbers?.unit_price ?? 0) / 100

    if (seatsDiscount !== 0) {
      lines.push({
        title: 'Discount: Seats',
        value: -seatsDiscount,
        color: 'var(--green-80)',
      })
    }
    if (numbersDiscount !== 0) {
      lines.push({
        title: 'Discount: Numbers',
        value: -numbersDiscount,
        color: 'var(--green-80)',
      })
    }

    return lines
  }

  getAnnualSavings = (planType: 'Basic' | 'Pro' | null) => {
    if (!planType) return 0
    const dropdownItems =
      planType === 'Basic'
        ? this.convertPlansToDropdownItems(this.getBasicPlans(this._period), this.selectedBasicPlan)
        : this.convertPlansToDropdownItems(this.getProPlans(this._period), this.selectedProPlan)

    const selectedValue = planType === 'Basic' ? this.selectedValues.Basic : this.selectedValues.Pro
    const selectedIndex = dropdownItems.findIndex((item) => item.id === selectedValue)
    if (selectedIndex === -1) return 0

    const monthlyPlans =
      planType === 'Basic' ? this.getBasicPlans('monthly') : this.getProPlans('monthly')
    const annualPlans =
      planType === 'Basic' ? this.getBasicPlans('annually') : this.getProPlans('annually')

    const monthlyPlan = monthlyPlans[selectedIndex]
    const annualPlan = annualPlans[selectedIndex]

    if (!monthlyPlan || !annualPlan) return 0

    const monthlyTotal = monthlyPlan.origin.monthly_price * 12
    const annualPrice = annualPlan.origin.price

    return Math.max(0, monthlyTotal - annualPrice)
  }
}
